import { AccButton } from "@components/base/AccButton";
import { AccInput } from "@components/base/AccInput";
import React, { useEffect, useState } from "react";
import Portal from "../../utils/portal";
import { MarkdownPageContext } from "@utils/MarkdownUtils";
import cn from "classnames";
import { isValidCommonEmail } from "@utils/emailValidation";
import { signupFormSubmit } from "./signupFormSubmit";
import { useModal } from "./ModalContext";
import { useErrorMessage } from "@utils/useErrorMessage";
import { graphql } from "gatsby";
import { DeepNonNullable } from "ts-essentials";
import { GatsbyImage } from "gatsby-plugin-image";

export const query = graphql`
  query SignUpModal($locale: String!) {
    strapiGenericText(locale: { eq: $locale }) {
      signupModal {
        header
        subHeader
        nudgingHeader
        oauthButtonPrefix
        orDivider
        emailPlaceholder
        signupCTA
      }
      noCreditCardNeeded
    }
    clients: strapiImage(collectionName: { eq: "clientWhiteLogos" }) {
      images {
        id
        alternativeText
        localFile {
          childImageSharp {
            gatsbyImageData(width: 120, placeholder: NONE)
          }
        }
      }
    }
    badges: strapiImage(collectionName: { eq: "socialProofModal" }) {
      images {
        id
        alternativeText
        localFile {
          childImageSharp {
            gatsbyImageData(width: 120, placeholder: NONE)
          }
        }
      }
    }
  }
`;

const SignupModal: React.FC<{
  data: DeepNonNullable<Queries.SignUpModalQuery>;
  nudging?: boolean;
  sliceContext: {
    locale: string;
  };
}> = ({ data }) => {
  const texts = {
    ...data.strapiGenericText.signupModal,
    noCreditCardNeeded: data.strapiGenericText.noCreditCardNeeded,
  };
  const { getErrorMessage } = useErrorMessage();
  const {
    showSignupErrorModal,
    isSignUpModalOpen,
    hideSignUpModal,
    showSignUpModal,
    showSignupCompleteModal,
  } = useModal();
  const [processing, setProcessing] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [emailInvalidCode, setEmailInvalidCode] = useState<string>("");

  useEffect(() => {
    const signup = new URLSearchParams(window.location.search).has("signup");
    if (signup) {
      showSignUpModal();
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const onInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const email = e.currentTarget.value;
    setEmail(email);
    const [valid, errorCode] = isValidCommonEmail(email ?? "");
    setEmailInvalidCode(valid ? "" : errorCode);
  };

  const handleClose = () => {
    hideSignUpModal();
  };

  const showSuccessModal = () => {
    showSignupCompleteModal(handleClose);
  };

  if (!isSignUpModalOpen) return null;
  return (
    <Portal>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto px-4 pt-4 sm:py-8"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="fixed inset-0 bg-gray-300 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={handleClose}
        />

        <div className="relative flex w-full max-w-md transform flex-col items-center overflow-hidden rounded-lg bg-accuRankerPurple-300 shadow-xl transition-all sm:max-w-xl">
          <div
            className="absolute right-2 top-1 z-10 cursor-pointer text-2xl text-gray-400"
            onClick={handleClose}
          >
            &#10006;
          </div>
          <div className="relative flex w-full flex-col items-start justify-center px-4 pt-8 sm:px-8">
            <p className="mb-3 w-full max-w-sm text-2xl tracking-tight text-white sm:max-w-full">
              <MarkdownPageContext>{texts?.header}</MarkdownPageContext>
            </p>
            <form
              onSubmit={e => {
                e.preventDefault();
                setProcessing(true);
                signupFormSubmit(
                  email,
                  showSuccessModal,
                  showSignupErrorModal,
                  getErrorMessage,
                  () => {
                    setProcessing(false);
                  }
                );
              }}
              className="mb-2 flex w-full max-w-sm flex-col items-center pt-5 sm:max-w-full"
            >
              <div className="relative flex w-full flex-col items-center justify-center gap-2 sm:flex-row sm:items-start">
                <div className="absolute bottom-full left-0 w-full pb-1 text-left text-xs font-medium text-red-500">
                  {email && getErrorMessage(emailInvalidCode)}
                </div>
                <AccInput
                  type="email"
                  required
                  name="email"
                  placeholder={texts?.emailPlaceholder || "Work email address"}
                  id="email"
                  className={cn(
                    "block w-full flex-1 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:w-auto sm:text-sm",
                    { "ring-2 ring-red-500": email && emailInvalidCode }
                  )}
                  onChange={onInputChange}
                />
                <div className="flex w-full flex-col items-center sm:w-auto">
                  <AccButton
                    id="signup-modal-email-submit"
                    disabled={!email || !!emailInvalidCode || processing}
                    type="submit"
                    className={cn(
                      "flex min-h-[38px] w-full items-center justify-center rounded-md bg-accuRankerGreen-500 px-4 py-2 font-medium text-white transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accuRankerGreen-600 focus:ring-offset-2 sm:text-sm md:text-base",
                      {
                        "cursor-not-allowed opacity-40":
                          !email || emailInvalidCode || processing,
                        "hover:bg-accuRankerGreen-600":
                          email && !emailInvalidCode,
                      }
                    )}
                  >
                    {texts?.signupCTA || "Signup"}
                  </AccButton>
                  <span className="mt-1 text-center text-xs text-gray-200">
                    {texts?.noCreditCardNeeded}
                  </span>
                </div>
              </div>
            </form>
          </div>
          <div className="relative flex w-full flex-col items-start justify-center px-4 pt-12 pb-5 sm:px-8">
            <div
              className="absolute left-0 top-0 -z-10 h-full w-full bg-[length:535px_171px] bg-no-repeat mix-blend-multiply"
              style={{
                backgroundImage:
                  "linear-gradient(0deg, rgba(255,255,255,1) 3%, rgba(255,255,255,0) 50%), url(/modal-bg.png)",
                backgroundSize: "535px 171px",
                backgroundPosition: "20px 2px",
              }}
            />
            <p className="mb-5 w-full max-w-sm text-lg text-gray-200 sm:max-w-full">
              {texts?.subHeader ||
                "Trusted by the world's leading brands and digital agencies"}
            </p>

            <div className="mb-[50px] flex w-full flex-row justify-around gap-4 md:justify-between">
              {data.clients.images.map((clientImage: any, idx: number) => (
                <GatsbyImage
                  key={clientImage.id}
                  image={
                    clientImage.localFile?.childImageSharp?.gatsbyImageData
                  }
                  className={cn("h-5", {
                    hidden: idx > 1,
                    "xs:block": idx === 2,
                    "sm:block": idx > 2,
                  })}
                  alt={clientImage.alternativeText}
                  imgStyle={{ objectFit: "contain" }}
                />
              ))}
            </div>
            <div className="flex w-full flex-row justify-around gap-4 md:justify-between">
              {data.badges.images.map((badgeImage: any, idx: number) => (
                <GatsbyImage
                  key={badgeImage.id}
                  image={badgeImage.localFile?.childImageSharp?.gatsbyImageData}
                  className={cn("h-[55px]", {
                    hidden: idx > 1,
                    "xs:block": idx === 2,
                    "sm:block": idx > 2,
                  })}
                  alt={badgeImage.alternativeText}
                  imgStyle={{ objectFit: "contain" }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default SignupModal;
